import { EChartsOption, ScatterSeriesOption, LineSeriesOption, BarSeriesOption } from 'echarts';
import { TranslateService } from '@ngx-translate/core';
import { UnitSettingsV2Service } from '@schenck/rotec-components';
import { MainCompare } from 'src/api';
import { isTruthy } from 'src/utils';
import { ChartColorService, ChartColors } from '../chart-color.service';
import { ChartConfig } from '../chart.service';

export type BalanceRun = 'initial' | 'last';

export interface PolarChartContent {
  /**
   * For which plane to show the data.
   */
  plane: number;
  /**
   * For which run to show the data.
   */
  run: BalanceRun;
  /**
   * In which direction the angle goes. Defaults to false.
   */
  clockwise?: boolean;
  /**
   * Starting angle. Defaults to 0.
   */
  startAngle?: number;
  /**
   * If set, draws a circle at given position showing a sigma.
   * If 0 or undefined, no circle is drawn.
   * Circle position and radius is inside data.
   * Defaults to undefined.
   */
  sigma?: 0 | 1 | 2 | 3;
  /**
   * If the tolerance circle should be shown
   */
  tolerance?: boolean;
  /**
   * The data
   */
  data: PolarChartData;
}

export interface PolarChartDataPoint {
  timestamp: string;
  rotorId: string;
  planes: {
    initialAbsolute: number;
    residualAbsolute: number;
    initialAngle: number;
    residualAngle: number;
  }[];
}

export type PolarChartData = {
  /**
   * For each plane, its tolerance absolute.
   */
  tolerances: number[];
} & MainCompare<{
  /**
   * For each plane, the sigma 1 tolerance circle definition
   */
  statistics: {
    initialAbsolute: number;
    initialAngle: number;
    initialSigma: number;
    residualAbsolute: number;
    residualAngle: number;
    residualSigma: number;
  }[];
  /**
   * From which asset the data comes.
   */
  asset: string;
  /**
   * The data points.
   */
  data: PolarChartDataPoint[];
}>;

export function getPolarChartOptions({
  content,
  translate,
  settings,
  darkMode,
  chartConfig,
}: {
  content: PolarChartContent;
  translate: TranslateService;
  settings: UnitSettingsV2Service;
  darkMode: boolean;
  chartConfig?: ChartConfig;
}): EChartsOption[] {
  const config = chartConfig || {};
  const unbalUnit = settings.state.units.unbalanceValue;
  const decimals = settings.state.units.decimals;

  return [
    {
      tooltip: {
        trigger: 'item',
      },
      legend: {
        orient: 'horizontal',
        type: 'scroll',
        bottom: 0,
        icon: 'circle',
        selected: config.selectedState ? config.selectedState : undefined,
      },
      polar: {
        radius: '70%', // to make space for legend
      },
      radiusAxis: {
        axisLabel: {
          hideOverlap: true,
        },
      },
      dataZoom: config.zoom
        ? [
            {
              type: 'inside',
              radiusAxisIndex: [0],
              filterMode: 'empty',
              start: config.zoomState ? config.zoomState.start : 0,
              end: config.zoomState ? config.zoomState.end : 100,
            },
          ]
        : [],
      angleAxis: {
        type: 'value',
        startAngle: content.startAngle !== undefined ? content.startAngle : 0,
        min: 0,
        max: 360,
        clockwise: content.clockwise || false,
      },
      series: [
        ...toleranceCircle(),
        ...dataPoints(content.data.main.data, content.data.main.asset, 0, darkMode),
        ...(content.data.compare
          ? dataPoints(content.data.compare.data, content.data.compare.asset, 2, darkMode)
          : []),
        ...addSigmaIfNecessary(),
      ],
    },
  ];

  /**
   * The data points. Red for nit, green for it.
   * Possibly the sigma, too.
   */
  function dataPoints(
    data: PolarChartDataPoint[],
    assetName: string,
    colorIdx: 0 | 2,
    darkMode = false
  ): [ScatterSeriesOption, ScatterSeriesOption] {
    const tolerance = content.data.tolerances[content.plane];
    const seriesBase: ScatterSeriesOption = {
      coordinateSystem: 'polar',
      type: 'scatter',
      tooltip: {
        formatter: ({ name, value }: any) => {
          return (
            `${translate.instant('MC_Rotor ID: VALUE', { value: name })}<br>` +
            `${value[0]} ${unbalUnit}<br>` +
            `${value[1]} °`
          );
        },
        confine: true,
      },
    };

    const dataValues = data
      .map((point) => {
        const plane = point.planes[content.plane];
        if (!plane) {
          return null;
        }

        const value =
          content.run === 'initial'
            ? [
                plane.initialAbsolute ? plane.initialAbsolute.toFixed(decimals) : null,
                plane.initialAngle ? plane.initialAngle.toFixed(decimals) : null,
              ]
            : [
                plane.residualAbsolute ? plane.residualAbsolute.toFixed(decimals) : null,
                plane.residualAngle ? plane.residualAngle.toFixed(decimals) : null,
              ];
        return {
          value,
          name: point.rotorId,
        };
      })
      .filter(isTruthy);

    return [
      {
        ...seriesBase,
        name: `${assetName}: ${translate.instant('MC_NIT')}`,
        itemStyle: {
          color: ChartColorService.getColors(ChartColors.RED, darkMode)[colorIdx],
        },
        data: dataValues.filter((point) => +point.value![0] > tolerance),
      },
      {
        ...seriesBase,
        name: `${assetName}: ${translate.instant('MC_IT')}`,
        itemStyle: {
          color: ChartColorService.getColors(ChartColors.GREEN, darkMode)[colorIdx],
        },
        data: dataValues.filter((point) => +point.value![0] <= tolerance),
      },
    ];
  }

  /**
   * Add sigma if user wants it by drawing a circle with a line series.
   */
  function addSigmaIfNecessary(): any {
    if (!content.sigma) {
      return [];
    }

    const sigma = content.data.main.statistics[content.plane];
    return [
      <LineSeriesOption>{
        type: 'line',
        coordinateSystem: 'polar',
        data:
          content.run === 'initial'
            ? drawCircle(
                sigma.initialAbsolute,
                sigma.initialAngle,
                sigma.initialSigma * content.sigma
              )
            : drawCircle(
                sigma.residualAbsolute,
                sigma.residualAngle,
                sigma.residualSigma * content.sigma
              ),
        areaStyle: { color: 'gray', opacity: 0.2 },
        lineStyle: { color: 'transparent' },
        itemStyle: { opacity: 0 },
        tooltip: { formatter: translate.instant('MC_Sigma Tolerance') },
      },
    ];
  }

  /**
   * The tolerance circle
   */
  function toleranceCircle(): [BarSeriesOption | ScatterSeriesOption] | [] {
    if (!content.tolerance) {
      return [];
    }

    return [
      {
        coordinateSystem: 'polar',
        type: 'bar',
        barWidth: '100%',
        data: [
          {
            value: content.data.tolerances[content.plane],
            itemStyle: {
              color: 'green',
              opacity: 0.2,
            },
            tooltip: { formatter: translate.instant('MC_Tolerance Region') },
          },
        ] as any[],
      },
    ];
  }
}

function drawCircle(absolute: number, angle: number, radius: number) {
  const circlePoints = [];
  const center: [number, number] = [absolute, angle];
  const items = 111;
  for (let i = 0; i < items; i++) {
    circlePoints.push(toRadAngle(xyAt(toXY(center), radius, (360 / items) * i)));
  }
  circlePoints.push(toRadAngle(xyAt(toXY(center), radius, 0))); // this makes the circle complete
  return circlePoints;
}

function toRadian(angle: number) {
  return (Math.PI * angle) / 180;
}

function toAngle(radian: number) {
  const res = (180 * radian) / Math.PI;
  if (res < 0) {
    return 360 + res;
  } else {
    return res;
  }
}

function angleFromXY(x: number, y: number) {
  let angle = Math.atan(y / x);
  if (x < 0) {
    angle += Math.PI;
  }
  return angle;
}

function toXY([rad, angle]: [number, number]): [number, number] {
  return [rad * Math.cos(toRadian(angle)), rad * Math.sin(toRadian(angle))];
}

function toRadAngle([x, y]: [number, number]): [number, number] {
  return [Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2)), toAngle(angleFromXY(x, y))];
}

function xyAt(
  [centerX, centerY]: [number, number],
  radius: number,
  angle: number
): [number, number] {
  const x = centerX + radius * Math.cos(toRadian(angle));
  const y = centerY + radius * Math.sin(toRadian(angle));
  return [x, y];
}
